import { AuthClient } from "@spp/auth";
import {
  AnyVaultFieldValue,
  getDefaultFieldState,
  VaultCardType,
  VaultExpiryDate,
  VaultFieldConfig,
  VaultFieldState,
  VaultFieldType,
  VaultSubmitRequest,
  VaultSubmitResponse,
} from "@spp/vault";
import valid from "card-validator";
import { getVaultApiHost } from "lib/env";

export const CARD_REVEAL_TIMEOUT = 60 * 1000; // 1m

export const getErrorMessage = (error: any): string =>
  typeof error === "string"
    ? error
    : typeof error?.message === "string"
    ? error.message
    : typeof error?.reason === "string"
    ? error.reason
    : typeof error?.reason?.message === "string"
    ? error.reason.message
    : "Unknown error";

export const isCardRevealField = (fieldType: VaultFieldType): boolean =>
  ["cardNumberReveal", "cardCvcReveal"].includes(fieldType);

export const formatCardNumber = (cardNumber: string): string =>
  cardNumber.split("").reduce((acc, char, i) => {
    const result = acc + char;
    return (i + 1) % 4 === 0 && i < cardNumber.length - 1
      ? result + " "
      : result;
  }, "");

export const parseFormattedValue = (value: string): string =>
  value.replace(/\s/g, "");

export const parseFieldValue = (
  fieldConfig: VaultFieldConfig,
  value: string
): AnyVaultFieldValue => {
  const parsedValue = parseFormattedValue(value);

  if (fieldConfig.fieldType === "expiryDate") {
    const { year, month } = valid.expirationDate(parsedValue);

    return {
      expiryMonth: month ?? "",
      expiryYear: year ?? "",
    };
  }

  return parsedValue;
};

export const getFieldState = (
  fieldConfig: VaultFieldConfig,
  cardType: VaultCardType | null,
  value: string
): VaultFieldState => {
  const parsedValue = parseFormattedValue(value);
  const isEmpty = !parsedValue;
  const isDirty = true;

  if (fieldConfig.fieldType === "cardNumber") {
    const { card, isValid, isPotentiallyValid } = valid.number(parsedValue);
    const bin = isValid ? parsedValue.substring(0, 6) : undefined;
    const last4 = isValid
      ? parsedValue.substring(parsedValue.length - 4, parsedValue.length)
      : undefined;

    return {
      isDirty,
      isEmpty,
      isValid,
      isPossible: isPotentiallyValid,
      cardType: card?.type as VaultCardType,
      bin,
      last4,
    };
  }
  if (fieldConfig.fieldType === "expiryDate") {
    const { isValid, isPotentiallyValid } = valid.expirationDate(parsedValue);
    return {
      isDirty,
      isEmpty,
      isValid,
      isPossible: isPotentiallyValid,
    };
  }
  if (fieldConfig.fieldType === "cardCvc") {
    const maxLength = ["visa", "mastercard"].includes(cardType ?? "")
      ? 3
      : cardType === "american-express"
      ? 4
      : [3, 4];
    const { isValid, isPotentiallyValid } = valid.cvv(parsedValue, maxLength);
    return {
      isDirty,
      isEmpty,
      isValid,
      isPossible: isPotentiallyValid,
    };
  }

  return getDefaultFieldState();
};

export const getInputMask = (
  fieldType: VaultFieldType,
  cardType?: VaultCardType | null
): string => {
  if (fieldType === "cardNumber") {
    return cardType === "american-express"
      ? "9999 999999 99999"
      : "9999 9999 9999 9999";
  }
  if (fieldType === "expiryDate") {
    return "99 / 99";
  }
  if (fieldType === "cardCvc") {
    return ["visa", "mastercard"].includes(cardType ?? "") ? "999" : "9999";
  }
  return "";
};

export const getVaultFieldFrames = (
  source: Window,
  vaultId: string
): Window[] => {
  const fieldFrames: Window[] = [];

  const frames = source.frames;
  for (let i = 0; i < frames.length; i++) {
    const frame = frames[i];

    let isSameOrigin = false;
    try {
      isSameOrigin = frame.location.origin === window.location.origin;
    } catch (error) {}

    if (isSameOrigin && frame.fieldConfig?.vaultId === vaultId) {
      fieldFrames.push(frame);
    }
  }

  return fieldFrames;
};

export type VaultFieldValues = { [fieldType: string]: AnyVaultFieldValue };
export const getVaultFieldValues = (
  source: Window,
  vaultId: string
): VaultFieldValues => {
  const fieldValues: VaultFieldValues = {};

  getVaultFieldFrames(source, vaultId).forEach((frame) => {
    if (!frame.fieldConfig) return;

    const input = frame.document.getElementsByTagName("input")[0];
    const value = parseFieldValue(frame.fieldConfig, input.value);
    fieldValues[frame.fieldConfig.fieldType] = value;

    if (
      frame.fieldConfig.fieldType === "expiryDate" &&
      typeof value === "object"
    ) {
      const { expiryMonth, expiryYear } = value as VaultExpiryDate;
      fieldValues.expiryMonth = expiryMonth;
      fieldValues.expiryYear = expiryYear;
    }
  });

  return fieldValues;
};

export const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const submitVault = async (
  submitRequest: VaultSubmitRequest,
  fieldValues: VaultFieldValues
): Promise<VaultSubmitResponse> => {
  const body = Object.keys(fieldValues).reduce(
    (body, fieldType) =>
      body.replace(
        new RegExp(escapeRegExp(`"{{${fieldType}}}"`), "g"),
        JSON.stringify(fieldValues[fieldType])
      ),
    submitRequest.body
  );

  return fetch(`${await getVaultApiHost()}${submitRequest.path}`, {
    method: submitRequest.method,
    body,
    headers: submitRequest.headers,
  })
    .then(async (response) => {
      let body = await response.text();
      return {
        response: {
          status: response.status,
          body,
          headers: Object.fromEntries(response.headers.entries()),
        },
      };
    })
    .catch((error) => {
      return {
        error: {
          message: getErrorMessage(error),
        },
      };
    });
};

export type GetDigitalCardRedactedRequest = {
  GetDigitalCardRedactedRequest: {
    digitalCreditCardId: string;
  };
};

export type GetDigitalCardUnredactedRequest = {
  GetDigitalCardUnredactedRequest: {
    digitalCreditCardId: string;
  };
};

export type DigitalCardDetails = {
  digitalCreditCardId: string;
  redactedPan: string;
  brand:
    | ""
    | "American Express"
    | "MasterCard"
    | "Visa"
    | "Diners Club"
    | "Discover"
    | "UnionPay"
    | "Maestro"
    | "Elo"
    | "Mir"
    | "Hiper"
    | "Hipercard"
    | "Jcb"
    | "Electron"
    | "Uatp";
  cardHolderName: string;
  emailAddress: string;
  phoneNumber: string;
  validFrom: string;
  validTo: string;
  limit: {
    minorCurrencyUnits: number;
    currency: string;
  };
  createdAt: string;
  creator: "Spenda" | "Conferma" | "ApgpayOnboarding" | "ApgpayPortal";
  status: "Active" | "Cancelled" | "Expired";
  usage?: "Single" | "Multiple";
  currencies?: string[];
  mccs?: string[];
  metadata: {};
};

export type GetDigitalCardRedactedResponse = {
  GetDigitalCardRedactedResponse: {
    details: DigitalCardDetails;
  };
};

export type GetDigitalCardUnredactedResponse = {
  GetDigitalCardUnredactedResponse: {
    pan: string;
    cvv?: string;
    details: DigitalCardDetails;
  };
};

export const administerDigitalCard = async (
  authClient: AuthClient,
  payload: GetDigitalCardRedactedRequest | GetDigitalCardUnredactedRequest
): Promise<{
  response?: GetDigitalCardRedactedResponse | GetDigitalCardUnredactedResponse;
  error?: string;
}> => {
  return fetch(`${await getVaultApiHost()}/issuer/card`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${await authClient.getToken()}`,
    },
  })
    .then(async (response) => {
      const json = await response.json();
      return response.ok
        ? { response: json }
        : { error: getErrorMessage(json) };
    })
    .catch((error) => ({ error: getErrorMessage(error) }));
};
